import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { colors, spacing, radius } from 'common/theme';
import { TextHot } from 'components/common/TextHot';
import { ButtonHot, BUTTON_MODE } from 'components/common/ButtonHot';
import { ModalHot } from 'components/common/ModalHot';
import ArchiveIcon from 'assets/personal_zone/Archive.svg';
import ExtractArchiveIcon from 'assets/personal_zone/Extract_Archive.svg';
import { IconHot } from 'components/common/IconHot';
import { 
  useChangeFreeCouponArchiveStatusMutation, 
  useChangePaidCouponArchiveStatusMutation 
} from 'redux/api/commerce.api';
import { useTranslations } from 'common/hooks/useTranslations';

export const ArchiveModal = ({
  open,
  onClose,
  onSuccess,
  onFailure,
  couponData,
  isPaid,
  testID,
}) => {
  const [changeFreeStatus, { isLoading: isFreeLoading }] = useChangeFreeCouponArchiveStatusMutation();
  const [changePaidStatus, { isLoading: isPaidLoading }] = useChangePaidCouponArchiveStatusMutation();
  const loading = isFreeLoading || isPaidLoading;
  const isCurrentlyArchived = couponData?.is_archived;
  const { translate } = useTranslations();

  let apiResult = null;
  const handleConfirm = async () => {
    try {
      if (isPaid) {
        apiResult = await changePaidStatus({ 
          orderId: Number(couponData.orderNumber), 
          orderItemId: Number(couponData.id), 
          toArchive: !isCurrentlyArchived 
        }).unwrap();
      } else {
        apiResult = await changeFreeStatus({ 
          couponId: couponData.code, 
          benefitId: couponData.benefitId, 
          toArchive: !isCurrentlyArchived 
        }).unwrap();
      }
      

      if (apiResult && apiResult.isCouponArchived != null) {
        onSuccess(apiResult?.isCouponArchived);
      } else {
        onFailure(apiResult.message);
      }
    } catch (e) {
      console.error("Archive status change failed", e);
    }
  };

  return (
    <ModalHot
      open={open}
      onClose={onClose}
      testID={testID}
    >
      {({ isMobile }) => (
        <Box
          dir="rtl"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            width: '100%',
          }}
        >
          {/* Icon */}
          <Box
            testId={`${testID}-icon`}
            sx={{
              width: 80,
              height: 80,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: `${spacing.lg}px`,
            }}
          >
            <IconHot
              src={isCurrentlyArchived ? ExtractArchiveIcon : ArchiveIcon}
              size={100}
            />
          </Box>

          {/* Title */}
          <TextHot
            testID={`${testID}-title`}
            size="xxl"
            bold="bold"
            color="primary"
            sx={{ marginBottom: `${spacing.md}px` }}
          >
            {isCurrentlyArchived ? translate('archive_modal_title_unarchive') : translate('archive_modal_title_archive')}
          </TextHot>

          {/* Description */}
          <TextHot
            testID={`${testID}-description`}
            size="l"
            color="subtle"
            isLtr={true}
            sx={{ marginBottom: `${spacing.xl}px`, maxWidth: 280 }}
          >
            {isCurrentlyArchived 
              ? translate('archive_modal_sub_title_unarchive')
              : translate('archive_modal_sub_title_archive')}
          </TextHot>

          {/* Buttons */}
          <Box
            testId={`${testID}-buttons`}
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row-reverse',
              gap: `${spacing.sm}px`,
              width: '100%',
            }}
          >
            <ButtonHot
              testID={`${testID}-confirm-btn`}
              mode={BUTTON_MODE.OUTLINED}
              onClick={handleConfirm}
              disabled={loading}
              sx={{
                flex: isMobile ? 'none' : 1,
                width: isMobile ? '100%' : 'auto',
                borderRadius: `${radius.xl}px`,
                padding: `${spacing.md}px`,
                border: `1px solid ${colors.system.border.default}`,
                color: colors.system.text.primary,
              }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : (isCurrentlyArchived ? translate('archive_modal_button_text_unarchive') : translate('archive_modal_button_text_archive'))}
            </ButtonHot>
            <ButtonHot
              testID={`${testID}-cancel-btn`}
              mode={BUTTON_MODE.PRIMARY}
              onClick={onClose}
              disabled={loading}
              sx={{
                flex: isMobile ? 'none' : 1,
                width: isMobile ? '100%' : 'auto',
                borderRadius: `${radius.xl}px`,
                padding: `${spacing.md}px`,
              }}
            >
              {translate('are_you_sure_modal_cancel_button_label')}
            </ButtonHot>
          </Box>
        </Box>
      )}
    </ModalHot>
  );
};

export default ArchiveModal;